/**
 * Billiards Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 */

import {
  BilliardsStatistics,
  BilliardsGameResult,
  BilliardsGameStats,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class BilliardsStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'billiards_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): BilliardsStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): BilliardsGameStats {
    return {
      wins: 0,
      losses: 0,
      winPercentage: 0,
      totalGames: 0,
      averageBallsPocketed: 0,
    };
  }

  getStatistics(): BilliardsStatistics {
    try {
      const stored = localStorage.getItem(this.STATS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore parse errors
    }
    return this.createEmptyStats();
  }

  recordResult(result: BilliardsGameResult): void {
    const stats = this.getStatistics();
    const isWin = result.winner === 'player1';

    if (result.mode === 'singleplayer' && result.difficulty) {
      const diffStats = stats.singlePlayer[result.difficulty];
      diffStats.totalGames++;
      if (isWin) {
        diffStats.wins++;
      } else {
        diffStats.losses++;
      }
      diffStats.winPercentage = diffStats.totalGames > 0
        ? (diffStats.wins / diffStats.totalGames) * 100
        : 0;
    } else if (result.mode === 'local-multiplayer') {
      stats.localMultiplayer.totalGames++;
      if (isWin) {
        stats.localMultiplayer.wins++;
      } else {
        stats.localMultiplayer.losses++;
      }
      stats.localMultiplayer.winPercentage = stats.localMultiplayer.totalGames > 0
        ? (stats.localMultiplayer.wins / stats.localMultiplayer.totalGames) * 100
        : 0;
    } else if (result.mode === 'multiplayer-realtime') {
      stats.onlineMultiplayer.totalGames++;
      if (isWin) {
        stats.onlineMultiplayer.wins++;
      } else {
        stats.onlineMultiplayer.losses++;
      }
      stats.onlineMultiplayer.winPercentage = stats.onlineMultiplayer.totalGames > 0
        ? (stats.onlineMultiplayer.wins / stats.onlineMultiplayer.totalGames) * 100
        : 0;
    }

    stats.totalGames++;
    stats.totalPlayTime += result.duration;

    try {
      localStorage.setItem(this.STATS_KEY, JSON.stringify(stats));
    } catch {
      // Storage full - ignore
    }
  }

  resetStatistics(): void {
    localStorage.removeItem(this.STATS_KEY);
  }
}
